import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Send, Check } from "lucide-react";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    // Client-only capture for now (no backend list). Persists in localStorage
    // so the user isn't nagged repeatedly if they subscribed already.
    try {
      const list = JSON.parse(localStorage.getItem("pdob_newsletter") || "[]");
      if (!list.includes(email)) list.push(email);
      localStorage.setItem("pdob_newsletter", JSON.stringify(list));
    } catch {
      /* ignore */
    }
    setSubscribed(true);
    setEmail("");
  };

  return (
    <footer className="bg-zinc-950 border-t-2 border-zinc-800 mt-20" data-testid="footer">
      {/* Newsletter strip */}
      <div className="border-b-2 border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 mb-2 uppercase">
              [ MAILING LIST · EARLY ACCESS ]
            </div>
            <h3 className="font-display text-3xl sm:text-4xl text-zinc-50 uppercase leading-none tracking-tight">
              Get on the list.
            </h3>
            <p className="text-zinc-400 text-sm mt-3 max-w-md">
              First access to every drop. Exclusive early codes. No spam — one email per drop.
            </p>
          </div>
          {subscribed ? (
            <div
              className="flex items-center gap-3 border-2 border-emerald-600/60 bg-emerald-950/30 px-4 py-3"
              data-testid="footer-newsletter-success"
            >
              <Check className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="font-display text-sm text-zinc-50 uppercase tracking-widest">You&apos;re In.</div>
                <div className="font-mono-tech text-[10px] text-zinc-500 uppercase mt-0.5">
                  // Watch your inbox for the next drop.
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-3"
              data-testid="footer-newsletter-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@survivor.com"
                data-testid="footer-newsletter-email"
                className="flex-1 bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 text-zinc-100 px-4 py-3 font-mono-tech text-sm outline-none transition-colors"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="bg-zinc-100 hover:bg-zinc-50 text-zinc-950 px-6 py-3 font-display text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2 transition-colors"
              >
                Subscribe <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="font-display text-3xl text-zinc-50 tracking-widest mb-3">
            PDOB<span className="text-zinc-200"> / STREET WEAR</span>
          </div>
          <p className="font-mono-tech text-xs text-zinc-500 uppercase max-w-xs leading-relaxed">
            // Premium streetwear. Limited drops. Never restocked.
          </p>
          <a
            href="https://instagram.com/pandemicstylesluxe"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            className="mt-5 inline-flex items-center gap-2 text-zinc-300 hover:text-zinc-100 font-mono-tech text-xs uppercase tracking-widest"
          >
            <Instagram className="w-4 h-4" /> @pandemicstylesluxe
          </a>
        </div>

        <div>
          <h4 className="font-display text-lg text-zinc-50 mb-3 tracking-widest">SHOP</h4>
          <ul className="space-y-1 text-sm font-mono-tech uppercase text-zinc-400">
            <li><Link to="/shop" className="hover:text-zinc-200">All</Link></li>
            <li><Link to="/shop?category=hoodie" className="hover:text-zinc-200">Hoodies</Link></li>
            <li><Link to="/shop?category=tee" className="hover:text-zinc-200">Tees</Link></li>
            <li><Link to="/shop?category=tracksuit" className="hover:text-zinc-200">Tracksuits</Link></li>
            <li><Link to="/shop?halloween=true" className="hover:text-zinc-200">Halloween</Link></li>
            <li><Link to="/coming-soon" className="hover:text-zinc-200">Coming Soon</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg text-zinc-50 mb-3 tracking-widest">HELP</h4>
          <ul className="space-y-1 text-sm font-mono-tech uppercase text-zinc-400">
            <li><Link to="/track" className="hover:text-zinc-200" data-testid="footer-track-order">Track Order</Link></li>
            <li><Link to="/contact" className="hover:text-zinc-200">Contact</Link></li>
            <li><Link to="/reviews" className="hover:text-zinc-200">Reviews</Link></li>
            <li><Link to="/contact#shipping" className="hover:text-zinc-200">Shipping</Link></li>
            <li><Link to="/contact#returns" className="hover:text-zinc-200">Returns</Link></li>
            <li><Link to="/contact#size-guide" className="hover:text-zinc-200">Size Guide</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg text-zinc-50 mb-3 tracking-widest">COMPANY</h4>
          <ul className="space-y-1 text-sm font-mono-tech uppercase text-zinc-400">
            <li><Link to="/contact#about" className="hover:text-zinc-200">About</Link></li>
            <li><Link to="/contact#care" className="hover:text-zinc-200">Care</Link></li>
            <li><Link to="/reviews#write" className="hover:text-zinc-200">Write a Review</Link></li>
            <li><Link to="/admin/login" className="hover:text-zinc-200 opacity-60">Admin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-zinc-900 py-4 text-center font-mono-tech text-xs text-zinc-600 uppercase">
        © {new Date().getFullYear()} PDOB / STREET WEAR // ALL RIGHTS RESERVED
      </div>
    </footer>
  );
};

export default Footer;
